import {
  AnimateLayoutChanges,
  defaultAnimateLayoutChanges,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  faCropSimple,
  faGrip,
  faRotateLeft,
  faRotateRight,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import Zoom from "react-medium-image-zoom";
import { Button, ButtonGroup, Card, CardBody, CardFooter } from "reactstrap";
import { SpinnerCircular } from "spinners-react";
import Auth from "../../auth/Auth";
import { SongsheetSlide } from "../../carousel/slide/SongsheetSlide";
import { DerivedImageUb } from "../../gql/graphql";
import { imageUrlForHash } from "../../helper/UrlResolver";
import { useDeleteDerivedImage } from "../useDeleteDerivedImage";
import { useRotateDerivedImage } from "../useRotateDerivedImage";
import { CyItem } from "./CyItem";
import { useUpdateDerivedImage } from "./useUpdateDerivedImage";
import { useTranslation } from "react-i18next";
import { ImageCropModal } from "./ImageCropModal";

interface Props {
  auth: Auth;
  derivedImage: DerivedImageUb;
  songId: string;
  bucket: string;
  isUpdatingOrder: boolean;
  reexecuteQuery: () => void;
  isDisabled?: boolean;
}

const animateLayoutChanges: AnimateLayoutChanges = (args) =>
  defaultAnimateLayoutChanges({ ...args, wasDragging: true });

export function DraggableImage({
  auth,
  derivedImage,
  songId,
  bucket,
  isUpdatingOrder,
  reexecuteQuery,
  isDisabled = false,
}: Props) {
  const { t } = useTranslation();
  const { deleteDerivedImage, fetching: deleteFetching } =
    useDeleteDerivedImage();
  const {
    rotateLeft,
    rotateRight,
    fetching: rotateFetching,
  } = useRotateDerivedImage();
  const { updateDerivedImage, fetching: updateFetching } =
    useUpdateDerivedImage(auth);
  const src = imageUrlForHash(bucket, songId, derivedImage.hash);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [isCropping, setIsCropping] = useState(false);
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);

  const isBusy =
    isDisabled ||
    isUpdatingOrder ||
    deleteFetching ||
    isDeleting ||
    updateFetching ||
    rotateFetching ||
    isRotating ||
    isCropping;

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: derivedImage.key,
      animateLayoutChanges: animateLayoutChanges,
      disabled: isBusy,
    });

  const handleDelete = async () => {
    if (isDisabled) {
      return;
    }

    if (!confirm(t("items.draggableImage.confirmDelete"))) {
      return;
    }

    setIsDeleting(true);
    const success = await deleteDerivedImage(bucket, songId, derivedImage.key);
    setIsDeleting(false);

    if (!success) {
      alert(t("items.draggableImage.deleteError"));
    }
  };

  const handleRotate = async (direction: "left" | "right") => {
    if (isDisabled) {
      return;
    }

    setIsRotating(true);
    try {
      const success =
        direction === "left"
          ? await rotateLeft(bucket, songId, derivedImage.key)
          : await rotateRight(bucket, songId, derivedImage.key);

      if (!success) {
        alert(t("items.draggableImage.rotateError"));
      }
    } catch (error) {
      console.error("Error rotating image:", error);
      alert(t("items.draggableImage.rotateError"));
    } finally {
      setIsRotating(false);
    }
  };

  const handleConfirmCrop = async (blob: Blob) => {
    if (isDisabled) {
      return;
    }

    setIsCropModalOpen(false);
    setIsCropping(true);
    try {
      const success = await updateDerivedImage(
        bucket,
        songId,
        derivedImage.key,
        blob,
      );
      if (!success) {
        alert(t("items.draggableImage.saveCropError"));
      } else {
        reexecuteQuery();
      }
    } catch (error) {
      console.error("Error cropping image:", error);
      alert(t("items.draggableImage.cropError"));
    } finally {
      setIsCropping(false);
    }
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} className="me-2" style={{ ...style }}>
      <Card
        className="pt-1"
        style={
          {
            // width: '18rem'
          }
        }
      >
        <Zoom>
          <SongsheetSlide
            style={{ width: "18rem" }}
            src={src}
            key={derivedImage.key}
          />
        </Zoom>
        <CardBody>
          <div className="d-flex justify-content-between">
            <Button
              color="primary"
              onClick={() => setIsCropModalOpen(true)}
              disabled={isBusy}
              data-cy={CyItem.CropButton}
            >
              <FontAwesomeIcon icon={faCropSimple} />
            </Button>
            <ButtonGroup>
              <Button
                color="primary"
                onClick={() => handleRotate("left")}
                disabled={isBusy}
                title={t("items.draggableImage.rotateLeft")}
                data-cy={CyItem.RotateLeftButton}
              >
                <FontAwesomeIcon icon={faRotateLeft} />
              </Button>
              <Button
                color="primary"
                onClick={() => handleRotate("right")}
                disabled={isBusy}
                title={t("items.draggableImage.rotateRight")}
                data-cy={CyItem.RotateRightButton}
              >
                <FontAwesomeIcon icon={faRotateRight} />
              </Button>
            </ButtonGroup>
            <Button
              color="primary"
              onClick={handleDelete}
              disabled={isBusy}
              data-cy={CyItem.DeleteDerivedImageButton}
            >
              <FontAwesomeIcon icon={faTrash} />
            </Button>
          </div>
        </CardBody>
        <div
          {...attributes}
          {...listeners}
          style={{ touchAction: "none" }}
          className="text-center"
          data-cy={CyItem.DragHandle}
        >
          <CardFooter>
            {isBusy ? (
              <SpinnerCircular
                color="#004085"
                style={{ height: "24" }}
                data-cy={CyItem.ChangeOrderLoadingSpinner}
              />
            ) : (
              <FontAwesomeIcon className="text-secondary" icon={faGrip} />
            )}
          </CardFooter>
        </div>
      </Card>
      <ImageCropModal
        src={src}
        isOpen={isCropModalOpen}
        onCancel={() => setIsCropModalOpen(false)}
        onConfirm={handleConfirmCrop}
      />
    </div>
  );
}
